import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { apiService } from '../../../config/api';
import { StudyService } from '../../../services/studyService';
import type { Study } from '../../../types';
import { ApiError } from '../../../utils/api';

export interface StudyEditValues {
  name: string;
  description: string;
  duration: string;
}

interface Props {
  study: Pick<Study, 'id' | 'name' | 'description' | 'duration'>;
  onClose: () => void;
  onSaved: (values: StudyEditValues) => void | Promise<void>;
  onError: (title: string, message: string, action?: string) => void;
  onChangePassword?: () => void;
  layout?: 'compact' | 'detailed';
}

// Mounted for each editing session so refreshes do not overwrite unsaved input.
export default function StudyEditDialog({
  study, onClose, onSaved, onError, onChangePassword, layout = 'compact',
}: Props) {
  const [form, setForm] = useState<StudyEditValues>(() => ({
    name: study.name,
    description: study.description || '',
    duration: study.duration || '',
  }));
  const [saving, setSaving] = useState(false);
  const detailed = layout === 'detailed';

  const save = async () => {
    const values = {
      name: form.name.trim(),
      description: form.description.trim(),
      duration: form.duration.trim(),
    };
    if (!values.name) {
      onError('Validation Error', 'Study name is required', 'Update Study');
      return;
    }

    setSaving(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        onError('Authentication Error', 'Authentication required to update study', 'Update Study');
        return;
      }
      // Empty optional fields are intentional: they clear previously saved values.
      const response = await StudyService.updateStudy(study.id, values, token);
      if (response.success) {
        await onSaved(values);
      } else {
        onError('Failed to Update Study', response.error || 'An error occurred while updating the study', 'Update Study');
      }
    } catch (error) {
      const message = error instanceof ApiError && error.details?.length
        ? error.details.map(detail => detail.message).join(' ')
        : error instanceof Error ? error.message : 'An unexpected error occurred while updating the study';
      onError('Failed to Update Study', message, 'Update Study');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Study</DialogTitle>
      <DialogContent>
        {detailed && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update study information and settings.
          </Typography>
        )}
        <Box sx={detailed ? { display: 'flex', flexDirection: 'column', gap: 2 } : undefined}>
          <TextField
            label="Study Name"
            fullWidth
            variant="outlined"
            required={detailed}
            autoFocus={!detailed}
            margin={detailed ? 'none' : 'dense'}
            value={form.name}
            onChange={event => setForm(current => ({ ...current, name: event.target.value }))}
            sx={detailed ? undefined : { mb: 2 }}
          />
          <TextField
            label={detailed ? 'Description' : 'Description (Optional)'}
            fullWidth
            multiline
            rows={detailed ? 4 : 3}
            variant="outlined"
            margin={detailed ? 'none' : 'dense'}
            value={form.description}
            onChange={event => setForm(current => ({ ...current, description: event.target.value }))}
            sx={detailed ? undefined : { mb: 2 }}
          />
          <TextField
            label="Study Duration (Optional)"
            fullWidth
            variant="outlined"
            margin={detailed ? 'none' : 'dense'}
            value={form.duration}
            onChange={event => setForm(current => ({ ...current, duration: event.target.value }))}
            placeholder="e.g., 4 weeks, 2 months, 6 months"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={onChangePassword ? { justifyContent: 'space-between', px: 3, pb: 2 } : undefined}>
        {onChangePassword && (
          <Button onClick={onChangePassword} variant="outlined" color="secondary">
            Change Password
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving || !form.name.trim()}>
            {saving ? 'Updating...' : 'Update Study'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
